import React, { useState, useEffect, useCallback } from "react";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { getAppUsers } from "../api/users";
import debounce from "lodash/debounce";
import { useAuth } from "../contexts/AuthContext";
import { roleHasPermission, PERMISSIONS } from "../config/adminRoles";

const Users = () => {
  const navigate = useNavigate();
  const { user: adminUser } = useAuth();
  const showUserActions = roleHasPermission(adminUser?.role, PERMISSIONS.USERS_ACTIONS);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [limit] = useState(10);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getAppUsers({
        page: currentPage,
        limit,
        search,
        sortBy,
        sortOrder,
      });
      setUsers(response.data || []);
      setTotalPages(response.pagination?.totalPages || 1);
      setTotalItems(response.pagination?.total || 0);
    } catch (error) {
      toast.error(error.message || "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchUsers = useCallback(
    debounce(() => fetchUsers(), 500),
    [currentPage, search, sortBy, sortOrder],
  );

  useEffect(() => {
    debouncedFetchUsers();
    return () => debouncedFetchUsers.cancel?.();
  }, [currentPage, search, sortBy, sortOrder]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return null;
    return sortOrder === "asc" ? (
      <ChevronUpIcon className="h-4 w-4 inline-block ml-1" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 inline-block ml-1" />
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Users</h1>
        <p className="text-gray-500 text-sm mt-1">Manage app users, their organizations, and companies.</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={handleSearch}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-72 focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                Client ID
              </th>
              <th
                className="px-5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs cursor-pointer hover:text-gray-700"
                onClick={() => handleSort("name")}
              >
                Name <SortIcon column="name" />
              </th>
              <th
                className="px-5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs cursor-pointer hover:text-gray-700"
                onClick={() => handleSort("email")}
              >
                Email <SortIcon column="email" />
              </th>
              <th className="px-5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                Organization
              </th>
              <th
                className="px-5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs cursor-pointer hover:text-gray-700"
                onClick={() => handleSort("createdAt")}
              >
                Created <SortIcon column="createdAt" />
              </th>
              <th className="px-5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                Verified
              </th>
              <th className="px-5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                Login
              </th>
              {showUserActions && (
                <th className="px-5 py-3 text-right font-medium text-gray-500 uppercase tracking-wider text-xs">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={showUserActions ? 8 : 7} className="text-center py-10 text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={showUserActions ? 8 : 7} className="text-center py-10 text-gray-400">
                  No users found
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user._id || user.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-4 max-w-[200px]">
                    <code className="text-xs text-gray-600 break-all">{user._id || user.id}</code>
                  </td>
                  <td className="px-5 py-4">
                    <Link
                      to={`/dashboard/users/${user._id || user.id}`}
                      className="font-medium text-primary hover:text-primary/80 hover:underline"
                    >
                      {user.name || "—"}
                    </Link>
                  </td>
                  <td className="px-5 py-4 text-gray-500">{user.email}</td>
                  <td className="px-5 py-4 text-gray-500">
                    {user?.organizationId?.name ?? "—"}
                  </td>
                  <td className="px-5 py-4 text-gray-500">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.isEmailVerified ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {user.isEmailVerified ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.isActive !== false ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                      }`}
                    >
                      {user.isActive !== false ? "Enabled" : "Disabled"}
                    </span>
                  </td>
                  {showUserActions && (
                    <td className="px-5 py-4 text-right space-x-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/users/${user._id || user.id}`)}
                        className="text-primary hover:text-primary/80 text-xs font-medium"
                      >
                        View
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>

        {/* Pagination - same UI as Companies */}
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-b-xl">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing{" "}
              <span className="font-medium">{(currentPage - 1) * limit + 1}</span> to{" "}
              <span className="font-medium">{Math.min(currentPage * limit, totalItems)}</span>{" "}
              of <span className="font-medium">{totalItems}</span> results
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {[...Array(Math.max(1, totalPages))].map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => setCurrentPage(index + 1)}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                    currentPage === index + 1
                      ? "z-10 bg-primary border-primary text-white"
                      : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  {index + 1}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </nav>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Users;
